import {useStoreActions, useStoreState} from 'easy-peasy';
import resolveToCallable from 'utils/resolveToCallable';
import useData from './useData';
import {useEffect} from 'react';

interface PaginationState {
    paginationPages: number[];
    paginationItemCount: number;
    paginationCurrentPage: number | null;
}

interface PaginationActions {
    setPaginationCurrentPage: (page: number) => void;
}

interface StoreResolverOrParams {
    storeResolver: string | ((state: any) => any);
    activePageNumber?: number;
    useLocalStore?: boolean;
    actionResolver?: string | ((actions: any) => any);
    dataResolver?: string | ((data: any) => any);
    requestParams?: {
        params?: Record<string, any>;
    };
}

interface UseDataParams {
    storeResolver: string | ((state: any) => any);
    actionResolver?: string | ((actions: any) => any);
    dataResolver?: string | ((data: any) => any);
    requestParams: {
        params: Record<string, any>;
    };
}

interface PaginatedDataReturn {
    data: any[];
    loading: boolean;
    error: any;
    pages: number[] | null;
    count: number;
    currentPage: number | null;
    setCurrentPage: (page: number) => void;
}

const usePaginatedData = (
    url: string,
    storeResolverOrParams: string | ((state: any) => any) | StoreResolverOrParams,
    actionResolver_?: string | ((actions: any) => any),
    dataResolver_?: string | ((data: any) => any),
): PaginatedDataReturn => {
    let storeResolver: (state: any) => any;
    let activePageNumber = 1;
    let useLocalStore = false;

    if (['function', 'string'].includes(typeof storeResolverOrParams)) {
        storeResolver = resolveToCallable(storeResolverOrParams as string | ((state: any) => any));
    } else if (typeof storeResolverOrParams === 'object') {
        const params = storeResolverOrParams as StoreResolverOrParams;
        useLocalStore = params.useLocalStore ?? false;

        if (useLocalStore) {
            throw Error('Local store option is not implemented for paginated data');
        }

        const storeResolverParam = params.storeResolver;
        activePageNumber = params.activePageNumber ?? activePageNumber;

        storeResolver = resolveToCallable(storeResolverParam);
    } else {
        throw new Error('Invalid storeResolverOrParams type');
    }

    const pages = useStoreState((state: any) => (storeResolver(state) as PaginationState).paginationPages);
    const count = useStoreState((state: any) => (storeResolver(state) as PaginationState).paginationItemCount);
    const currentPage = useStoreState((state: any) => (storeResolver(state) as PaginationState).paginationCurrentPage);

    const setCurrentPage = useStoreActions((actions: any) => (storeResolver(actions) as PaginationActions).setPaginationCurrentPage);

    useEffect(() => {
        if (currentPage === null) {
            setCurrentPage(activePageNumber);
        }
    }, [currentPage, setCurrentPage, activePageNumber]);

    let useDataParams: UseDataParams;
    if (['function', 'string'].includes(typeof storeResolverOrParams)) {
        useDataParams = {
            storeResolver: storeResolverOrParams as string | ((state: any) => any),
            actionResolver: actionResolver_,
            dataResolver: dataResolver_,
            requestParams: {
                params: {
                    page: currentPage,
                },
            },
        };
    } else if (typeof storeResolverOrParams === 'object') {
        const params = storeResolverOrParams as StoreResolverOrParams;
        useDataParams = {
            storeResolver: params.storeResolver,
            actionResolver: params.actionResolver,
            dataResolver: params.dataResolver,
            requestParams: {
                params: {
                    ...params.requestParams?.params,
                    page: currentPage,
                },
            },
        };
    } else {
        throw new Error('Invalid storeResolverOrParams type');
    }

    useDataParams.actionResolver = useDataParams.actionResolver || 'storePaginatedData';

    const [data, loading, storeAction, error] = useData(url, useDataParams);

    if (error !== null) {
        return {
            data: Array.isArray(data) ? data : [],
            loading,
            error,
            pages: null,
            count: 0,
            currentPage,
            setCurrentPage: () => {},
        };
    }
    
    return {
        data: Array.isArray(data) ? data : [],
        loading,
        error,
        pages,
        count,
        currentPage,
        setCurrentPage,
    };
};

export default usePaginatedData; 